import { useState, useEffect } from 'react'
import {
    Plus, Search, Building2, DollarSign,
    Eye,
    Grid3x3, List
} from 'lucide-react'
import { useToast } from '../hooks/useToast'
import { supabase } from '../services/supabase'
import Button from '../components/Button'
import { ToastContainer } from '../components/Toast'
import { CurrencyInputV2 } from '../components/CurrencyInputV2'
import ImoveisCardView from '../components/ImoveisCardView'
import ImoveisListView from '../components/ImoveisListView'

interface Imovel {
    id: string
    nome: string
    endereco: string | null
    estado: string | null
    cidade: string | null
    tipo: string
    tipo_imovel: string
    valor_venda: number | null
    construtora_id: string | null
    unidades_disponiveis: number
    unidades_vendidas: number
    created_at: string
    construtora?: {
        nome: string
        cnpj: string
        contato_responsavel: string
    }
}

export default function AdminImoveis() {
    console.log('🎬 AdminImoveis: Componente iniciado')

    const { toasts, showSuccess, showError } = useToast()
    const [imoveis, setImoveis] = useState<Imovel[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

    // Filtros
    const [filtroTipo, setFiltroTipo] = useState<string>('todos')
    const [filtroCategoria, setFiltroCategoria] = useState<string>('todos')
    const [filtroConstrutora, setFiltroConstrutora] = useState<string>('todos')
    const [filtroEstado, setFiltroEstado] = useState<string>('todos')
    const [filtroCidade, setFiltroCidade] = useState<string>('todos')
    const [filtroValorMin, setFiltroValorMin] = useState<string>('')
    const [filtroValorMax, setFiltroValorMax] = useState<string>('')

    // Dados para os selects
    const [construtoras, setConstrutoras] = useState<Array<{ id: string, nome: string }>>([])
    const [estadosUnicos, setEstadosUnicos] = useState<string[]>([])
    // Carregar viewMode do localStorage ou usar 'cards' como padrão
    const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
        const saved = localStorage.getItem('admin_imoveis_viewMode')
        return (saved === 'cards' || saved === 'list') ? saved : 'cards'
    })
    // Paginação: exibir 12 itens por vez
    const [itemsToShow, setItemsToShow] = useState(12)
    // Ordenação
    const [sortColumn, setSortColumn] = useState<string | null>(null)
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
    // Colunas visíveis (apenas para list view) - carregar do localStorage
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
        const saved = localStorage.getItem('admin_imoveis_visibleColumns')
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                // Garantir que todas as colunas padrão existam
                return {
                    imovel: true,
                    localizacao: true,
                    tipo: true,
                    construtora: true,
                    valor: true,
                    unidades: true,
                    cadastro: true,
                    acoes: true,
                    ...parsed
                }
            } catch (e) {
                console.error('Erro ao carregar colunas visíveis do localStorage:', e)
            }
        }
        return {
            imovel: true,
            localizacao: true,
            tipo: true,
            construtora: true,
            valor: true,
            unidades: true,
            cadastro: true,
            acoes: true
        }
    })

    console.log('🎬 AdminImoveis: Estados iniciais:', {
        imoveisCount: imoveis.length,
        loading,
        searchTerm
    })

    useEffect(() => {
        console.log('🔄 AdminImoveis: useEffect executado - Componente montado')
        console.log('🔄 Chamando loadImoveis()...')
        loadImoveis()
        loadConstrutoras()
    }, [])

    // Salvar viewMode no localStorage quando mudar
    useEffect(() => {
        localStorage.setItem('admin_imoveis_viewMode', viewMode)
    }, [viewMode])

    // Salvar colunas visíveis no localStorage quando mudarem
    useEffect(() => {
        localStorage.setItem('admin_imoveis_visibleColumns', JSON.stringify(visibleColumns))
    }, [visibleColumns])

    // Resetar paginação quando filtros mudarem
    useEffect(() => {
        setItemsToShow(6)
    }, [searchTerm, filtroTipo, filtroCategoria, filtroConstrutora, filtroEstado, filtroCidade, filtroValorMin, filtroValorMax])

    // Carregar construtoras para o filtro
    const loadConstrutoras = async () => {
        try {
            console.log('🔍 Carregando construtoras para filtro...')

            // Tentar primeiro com função RPC (se existir) para contornar RLS
            let construtorasData = null
            let construtorasError = null

            try {
                const { data: rpcData, error: rpcError } = await supabase.rpc('buscar_construtoras_admin')
                if (!rpcError && rpcData) {
                    console.log('✅ Função RPC buscar_construtoras_admin encontrada e executada!')
                    construtorasData = rpcData
                } else {
                    if (rpcError) {
                        console.log('ℹ️ Função RPC retornou erro:', rpcError)
                    }
                    console.log('📋 Usando query direta para buscar construtoras...')
                    const result = await supabase
                        .from('construtoras')
                        .select('id, nome')
                        .order('nome')
                    construtorasData = result.data
                    construtorasError = result.error
                }
            } catch (rpcErr) {
                console.log('ℹ️ Função RPC não disponível, usando query direta...')
                const result = await supabase
                    .from('construtoras')
                    .select('id, nome')
                    .order('nome')
                construtorasData = result.data
                construtorasError = result.error
            }

            if (construtorasError) {
                console.error('❌ Erro ao buscar construtoras:', construtorasError)
                throw construtorasError
            }

            console.log('✅ Construtoras carregadas:', construtorasData?.length || 0)
            setConstrutoras(construtorasData || [])
        } catch (err) {
            console.error('❌ Erro ao carregar construtoras:', err)
            // Não mostrar erro ao usuário, apenas logar
        }
    }

    // Atualizar estados únicos quando imóveis mudarem
    useEffect(() => {
        const estados = [...new Set(imoveis.map(i => i.estado).filter(Boolean))].sort()
        setEstadosUnicos(estados as string[])
    }, [imoveis])

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            const dropdown = target.closest('.dropdown-container')

            if (activeDropdown && !dropdown) {
                setActiveDropdown(null)
            }
        }

        if (activeDropdown) {
            document.addEventListener('click', handleClickOutside)
        }

        return () => {
            document.removeEventListener('click', handleClickOutside)
        }
    }, [activeDropdown])

    const loadImoveis = async () => {
        try {
            console.log('🚀 ========== INÍCIO loadImoveis ==========')
            setLoading(true)

            // Passo 1: Verificar autenticação do admin
            console.log('📋 Passo 1: Verificando autenticação...')
            const { data: { user }, error: authError } = await supabase.auth.getUser()

            console.log('📋 Resultado auth.getUser():', {
                user: user ? { id: user.id, email: user.email } : null,
                authError
            })

            if (authError) {
                console.error('❌ Erro de autenticação:', authError)
                throw new Error(`Erro de autenticação: ${authError.message}`)
            }

            if (!user) {
                console.error('❌ Nenhum usuário logado')
                throw new Error('Usuário não logado')
            }

            console.log('✅ Usuário autenticado:', { id: user.id, email: user.email })

            // Passo 2: Verificar se é admin
            console.log('📋 Passo 2: Verificando se é admin...')
            const { data: { session } } = await supabase.auth.getSession()
            console.log('📋 Session:', session ? 'Existe' : 'Não existe')

            // Passo 3: Testar se consegue contar imóveis (verificar RLS)
            console.log('📋 Passo 3a: Testando contagem de imóveis (verificar RLS)...')
            const { count: imoveisCount, error: countError } = await supabase
                .from('imoveis')
                .select('*', { count: 'exact', head: true })

            console.log('📋 Resultado da contagem:', { count: imoveisCount, error: countError })

            if (countError) {
                console.warn('⚠️ Erro ao contar imóveis (possível problema de RLS):', countError)
            } else {
                console.log('✅ Contagem bem-sucedida. Total de imóveis no banco:', imoveisCount)
            }

            // Passo 3b: Tentar buscar imóveis
            console.log('📋 Passo 3b: Preparando query para buscar imóveis...')
            const query = supabase
                .from('imoveis')
                .select(`
                    id,
                    nome,
                    endereco,
                    estado,
                    cidade,
                    tipo,
                    tipo_imovel,
                    valor_venda,
                    construtora_id,
                    unidades_disponiveis,
                    unidades_vendidas,
                    created_at,
                    construtora:construtoras (
                        nome,
                        cnpj,
                        contato_responsavel
                    )
                `)
                .order('created_at', { ascending: false })

            // Tentar primeiro com função RPC (se existir) para contornar RLS
            console.log('📋 Tentando buscar com função RPC (se existir)...')
            let imoveisData = null
            let imoveisError = null
            let usandoRPC = false

            try {
                const { data: rpcData, error: rpcError } = await supabase.rpc('buscar_imoveis_admin')
                if (!rpcError && rpcData) {
                    console.log('✅ Função RPC encontrada e executada com sucesso!')
                    console.log('📊 Dados RPC:', rpcData)
                    usandoRPC = true
                    imoveisData = rpcData
                } else {
                    if (rpcError) {
                        console.log('ℹ️ Função RPC retornou erro:', rpcError)
                    } else {
                        console.log('ℹ️ Função RPC não retornou dados')
                    }
                    console.log('📋 Usando query direta...')
                    console.log('📋 Query preparada, executando...')
                    const result = await query
                    imoveisData = result.data
                    imoveisError = result.error
                }
            } catch (rpcErr) {
                console.log('ℹ️ Função RPC não disponível (erro ao chamar):', rpcErr)
                console.log('📋 Usando query direta...')
                console.log('📋 Query preparada, executando...')
                const result = await query
                imoveisData = result.data
                imoveisError = result.error
            }

            console.log('📊 ========== RESULTADO DA QUERY ==========')
            console.log('📊 imoveisData:', imoveisData)
            console.log('📊 Tipo de imoveisData:', typeof imoveisData)
            console.log('📊 É array?', Array.isArray(imoveisData))
            console.log('📊 Quantidade:', imoveisData?.length || 0)
            console.log('📊 imoveisError:', imoveisError)
            console.log('📊 Detalhes do erro:', imoveisError ? {
                message: imoveisError.message,
                details: imoveisError.details,
                hint: imoveisError.hint,
                code: imoveisError.code
            } : null)

            if (imoveisError) {
                console.error('❌ ========== ERRO DETALHADO ==========')
                console.error('❌ Código do erro:', imoveisError.code)
                console.error('❌ Mensagem:', imoveisError.message)
                console.error('❌ Detalhes:', imoveisError.details)
                console.error('❌ Hint:', imoveisError.hint)
                console.error('❌ Erro completo:', JSON.stringify(imoveisError, null, 2))
                throw imoveisError
            }

            // Passo 4: Processar dados
            console.log('📋 Passo 4: Processando dados...')
            console.log('📋 Dados brutos recebidos:', imoveisData)

            if (!imoveisData) {
                console.warn('⚠️ imoveisData é null ou undefined')
                setImoveis([])
                return
            }

            if (!Array.isArray(imoveisData)) {
                console.warn('⚠️ imoveisData não é um array:', typeof imoveisData)
                setImoveis([])
                return
            }

            console.log('📋 Processando', imoveisData.length, 'imóveis...')
            console.log('📋 Usando RPC?', usandoRPC)

            const imoveisProcessados = imoveisData.map((imovel, index) => {
                console.log(`📋 Processando imóvel ${index + 1}/${imoveisData.length}:`, {
                    id: imovel.id,
                    nome: imovel.nome,
                    construtora: imovel.construtora,
                    tipoConstrutora: typeof imovel.construtora
                })

                // Se veio do RPC, construtora já é um objeto JSONB
                // Se veio da query direta, pode ser array ou objeto
                let construtoraProcessada = null
                if (usandoRPC) {
                    // RPC retorna construtora como JSONB (objeto)
                    construtoraProcessada = imovel.construtora
                } else {
                    // Query direta pode retornar como array ou objeto
                    construtoraProcessada = Array.isArray(imovel.construtora)
                        ? imovel.construtora[0]
                        : imovel.construtora
                }

                return {
                    ...imovel,
                    construtora: construtoraProcessada
                }
            })

            console.log('✅ ========== PROCESSAMENTO CONCLUÍDO ==========')
            console.log('✅ Total de imóveis processados:', imoveisProcessados.length)
            console.log('✅ Primeiro imóvel (exemplo):', imoveisProcessados[0])

            setImoveis(imoveisProcessados)
            console.log('✅ Estado atualizado com', imoveisProcessados.length, 'imóveis')
        } catch (err) {
            console.error('❌ ========== ERRO CAPTURADO ==========')
            console.error('❌ Tipo do erro:', typeof err)
            console.error('❌ É instância de Error?', err instanceof Error)
            console.error('❌ Erro completo:', err)
            console.error('❌ Stack trace:', err instanceof Error ? err.stack : 'N/A')

            if (err && typeof err === 'object') {
                console.error('❌ Propriedades do erro:', Object.keys(err))
                console.error('❌ Erro serializado:', JSON.stringify(err, null, 2))
            }

            const errorMessage = err instanceof Error
                ? err.message
                : (err && typeof err === 'object' && 'message' in err)
                    ? String(err.message)
                    : 'Erro desconhecido'

            console.error('❌ Mensagem de erro final:', errorMessage)
            showError(`Erro ao carregar imóveis: ${errorMessage}`)
        } finally {
            console.log('🏁 ========== FIM loadImoveis ==========')
            setLoading(false)
        }
    }

    // Verificar se há filtros ativos
    const hasActiveFilters = searchTerm ||
        filtroTipo !== 'todos' ||
        filtroCategoria !== 'todos' ||
        filtroConstrutora !== 'todos' ||
        filtroEstado !== 'todos' ||
        filtroCidade !== 'todos' ||
        filtroValorMin ||
        filtroValorMax

    const filteredImoveis = imoveis.filter(imovel => {
        // Filtro de busca por texto
        const matchSearch = !searchTerm ||
            imovel.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (imovel.endereco || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (imovel.cidade && imovel.cidade.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (imovel.estado && imovel.estado.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (imovel.construtora?.nome && imovel.construtora.nome.toLowerCase().includes(searchTerm.toLowerCase()))

        // Filtro por tipo
        const matchTipo = filtroTipo === 'todos' || imovel.tipo === filtroTipo

        // Filtro por categoria (tipo_imovel)
        const matchCategoria = filtroCategoria === 'todos' || imovel.tipo_imovel === filtroCategoria

        // Filtro por construtora
        const matchConstrutora = filtroConstrutora === 'todos' || imovel.construtora_id === filtroConstrutora

        // Filtro por estado
        const matchEstado = filtroEstado === 'todos' || imovel.estado === filtroEstado

        // Filtro por cidade
        const matchCidade = filtroCidade === 'todos' || imovel.cidade === filtroCidade

        // Filtro por valor mínimo
        const valorMin = filtroValorMin ? parseFloat(filtroValorMin.replace(/[^\d,]/g, '').replace(',', '.')) : 0
        const matchValorMin = !filtroValorMin || (imovel.valor_venda && imovel.valor_venda >= valorMin)

        // Filtro por valor máximo
        const valorMax = filtroValorMax ? parseFloat(filtroValorMax.replace(/[^\d,]/g, '').replace(',', '.')) : Infinity
        const matchValorMax = !filtroValorMax || (imovel.valor_venda && imovel.valor_venda <= valorMax)

        return matchSearch && matchTipo && matchCategoria && matchConstrutora &&
            matchEstado && matchCidade && matchValorMin && matchValorMax
    })

    // Imóveis para estatísticas: usar filteredImoveis se houver filtros, senão usar todos os imóveis
    const imoveisParaEstatisticas = hasActiveFilters ? filteredImoveis : imoveis

    // Função de ordenação
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            // Se já está ordenando por esta coluna, inverte a direção
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            // Nova coluna, começa com ascendente
            setSortColumn(column)
            setSortDirection('asc')
        }
    }

    // Aplicar ordenação aos imóveis filtrados
    const sortedImoveis = [...filteredImoveis].sort((a, b) => {
        if (!sortColumn) return 0

        let aValue: any
        let bValue: any

        switch (sortColumn) {
            case 'nome':
                aValue = a.nome.toLowerCase()
                bValue = b.nome.toLowerCase()
                break
            case 'cidade':
                aValue = (a.cidade || '').toLowerCase()
                bValue = (b.cidade || '').toLowerCase()
                break
            case 'tipo':
                aValue = a.tipo.toLowerCase()
                bValue = b.tipo.toLowerCase()
                break
            case 'construtora':
                aValue = (a.construtora?.nome || '').toLowerCase()
                bValue = (b.construtora?.nome || '').toLowerCase()
                break
            case 'valor_venda':
                aValue = a.valor_venda || 0
                bValue = b.valor_venda || 0
                break
            case 'unidades_disponiveis':
                aValue = a.unidades_disponiveis || 0
                bValue = b.unidades_disponiveis || 0
                break
            case 'created_at':
                aValue = new Date(a.created_at).getTime()
                bValue = new Date(b.created_at).getTime()
                break
            default:
                return 0
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
    })

    // Função para alternar visibilidade de colunas
    const handleToggleColumn = (column: string) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }))
    }

    // Log quando o estado de imoveis muda
    useEffect(() => {
        console.log('📊 AdminImoveis: Estado imoveis atualizado:', {
            total: imoveis.length,
            filtered: filteredImoveis.length,
            searchTerm,
            loading
        })
    }, [imoveis, filteredImoveis, searchTerm, loading])


    // Funções para os componentes de visualização
    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('pt-BR')
    }

    const handleView = (imovel: Imovel) => {
        // TODO: Implementar visualização de detalhes do imóvel
        console.log('Visualizar imóvel:', imovel)
        showSuccess(`Visualizando imóvel: ${imovel.nome}`)
    }

    const handleEdit = (imovel: Imovel) => {
        // TODO: Implementar edição do imóvel
        console.log('Editar imóvel:', imovel)
        showSuccess(`Editando imóvel: ${imovel.nome}`)
    }

    const handleDelete = (imovel: Imovel) => {
        // TODO: Implementar exclusão do imóvel
        if (confirm(`Tem certeza que deseja excluir o imóvel "${imovel.nome}"?`)) {
            console.log('Excluir imóvel:', imovel)
            showSuccess(`Imóvel "${imovel.nome}" será excluído`)
        }
    }

    console.log('🎨 AdminImoveis: Renderizando componente', {
        loading,
        totalImoveis: imoveis.length,
        filteredImoveis: filteredImoveis.length,
        searchTerm
    })

    if (loading) {
        console.log('⏳ AdminImoveis: Mostrando loading...')
        return (
            <div className="w-full">
                <div className="w-full">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    console.log('✅ AdminImoveis: Renderizando conteúdo principal')
    return (
        <div className="w-full">
            <div className="w-full">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                Imóveis
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Gerencie todos os imóveis cadastrados no sistema
                            </p>
                        </div>
                        <Button
                            variant="btn_add"
                            className="flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Novo Imóvel
                        </Button>
                    </div>
                </div>

                {/* Estatísticas */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Total de Imóveis
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                                    {imoveisParaEstatisticas.length}
                                </p>
                            </div>
                            <Building2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Unidades Disponíveis
                                </p>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                                    {imoveisParaEstatisticas.reduce((sum, imovel) => sum + (imovel.unidades_disponiveis || 0), 0)}
                                </p>
                            </div>
                            <Eye className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Unidades Vendidas
                                </p>
                                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                                    {imoveisParaEstatisticas.reduce((sum, imovel) => sum + (imovel.unidades_vendidas || 0), 0)}
                                </p>
                            </div>
                            <DollarSign className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Total de Unidades
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                                    {imoveisParaEstatisticas.reduce((sum, imovel) =>
                                        sum + (imovel.unidades_disponiveis || 0) + (imovel.unidades_vendidas || 0),
                                        0
                                    )}
                                </p>
                            </div>
                            <Building2 className="h-8 w-8 text-gray-600 dark:text-gray-400" />
                        </div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Filtros
                    </h3>

                    <div className="flex flex-col sm:flex-row gap-4 mb-4">
                        {/* Busca por texto */}
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                                <input
                                    type="text"
                                    placeholder="Buscar imóveis por nome, endereço, cidade, estado ou construtora..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                />
                            </div>
                        </div>
                        {/* Toggle de Visualização */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('cards')}
                                    className={`p-2 rounded-md transition-colors ${viewMode === 'cards'
                                        ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                        }`}
                                    title="Visualização em Cards"
                                >
                                    <Grid3x3 size={20} />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                                        ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                        }`}
                                    title="Visualização em Lista"
                                >
                                    <List size={20} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Grid de filtros */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {/* Filtro Tipo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Tipo
                            </label>
                            <select
                                value={filtroTipo}
                                onChange={(e) => setFiltroTipo(e.target.value)}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="todos">Todos os tipos</option>
                                <option value="apartamento">Apartamento</option>
                                <option value="casa">Casa</option>
                                <option value="terreno">Terreno</option>
                                <option value="comercial">Comercial</option>
                                <option value="rural">Rural</option>
                            </select>
                        </div>

                        {/* Filtro Categoria */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Categoria
                            </label>
                            <select
                                value={filtroCategoria}
                                onChange={(e) => setFiltroCategoria(e.target.value)}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="todos">Todas</option>
                                <option value="novo">Novo</option>
                                <option value="usado">Usado</option>
                            </select>
                        </div>

                        {/* Filtro Construtora */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Construtora
                            </label>
                            <select
                                value={filtroConstrutora}
                                onChange={(e) => setFiltroConstrutora(e.target.value)}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="todos">Todas</option>
                                {construtoras.map(construtora => (
                                    <option key={construtora.id} value={construtora.id}>
                                        {construtora.nome}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Filtro Estado */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Estado
                            </label>
                            <select
                                value={filtroEstado}
                                onChange={(e) => {
                                    setFiltroEstado(e.target.value)
                                    setFiltroCidade('todos') // Resetar cidade quando mudar estado
                                }}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="todos">Todos</option>
                                {estadosUnicos.map(estado => (
                                    <option key={estado} value={estado}>
                                        {estado}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Filtro Cidade */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Cidade
                            </label>
                            <select
                                value={filtroCidade}
                                onChange={(e) => setFiltroCidade(e.target.value)}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                disabled={filtroEstado === 'todos'}
                            >
                                <option value="todos">Todas</option>
                                {filtroEstado !== 'todos' && imoveis
                                    .filter(i => i.estado === filtroEstado && i.cidade)
                                    .map(i => i.cidade)
                                    .filter((cidade): cidade is string => cidade !== null)
                                    .filter((cidade, index, self) => self.indexOf(cidade) === index)
                                    .sort()
                                    .map(cidade => (
                                        <option key={cidade} value={cidade}>
                                            {cidade}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        {/* Filtro Valor Mínimo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Valor Mínimo
                            </label>
                            <div className="w-full">
                                <CurrencyInputV2
                                    value={filtroValorMin}
                                    onChange={(value) => setFiltroValorMin(value)}
                                    placeholder="R$ 0,00"
                                    className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                        </div>

                        {/* Filtro Valor Máximo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Valor Máximo
                            </label>
                            <div className="w-full">
                                <CurrencyInputV2
                                    value={filtroValorMax}
                                    onChange={(value) => setFiltroValorMax(value)}
                                    placeholder="R$ 0,00"
                                    className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                        </div>

                        {/* Botão Limpar Filtros */}
                        <div className="flex items-end">
                            <Button
                                variant="btn_secondary"
                                onClick={() => {
                                    setFiltroTipo('todos')
                                    setFiltroCategoria('todos')
                                    setFiltroConstrutora('todos')
                                    setFiltroEstado('todos')
                                    setFiltroCidade('todos')
                                    setFiltroValorMin('')
                                    setFiltroValorMax('')
                                    setSearchTerm('')
                                }}
                                className="w-full"
                            >
                                Limpar Filtros
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Resultado dos Filtros */}
                {(searchTerm || filtroTipo !== 'todos' || filtroCategoria !== 'todos' ||
                    filtroConstrutora !== 'todos' || filtroEstado !== 'todos' ||
                    filtroCidade !== 'todos' || filtroValorMin || filtroValorMax) && (
                        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                            Mostrando <strong className="text-gray-900 dark:text-gray-100">{filteredImoveis.length}</strong> de <strong className="text-gray-900 dark:text-gray-100">{imoveis.length}</strong> imóveis
                        </div>
                    )}

                {/* Lista de Imóveis */}
                {filteredImoveis.length === 0 ? (
                    <div className="text-center py-16">
                        <Building2 className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                            {searchTerm || filtroTipo !== 'todos' || filtroCategoria !== 'todos' ||
                                filtroConstrutora !== 'todos' || filtroEstado !== 'todos' ||
                                filtroCidade !== 'todos' || filtroValorMin || filtroValorMax
                                ? 'Nenhum imóvel encontrado'
                                : 'Nenhum imóvel cadastrado'}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                            {searchTerm || filtroTipo !== 'todos' || filtroCategoria !== 'todos' ||
                                filtroConstrutora !== 'todos' || filtroEstado !== 'todos' ||
                                filtroCidade !== 'todos' || filtroValorMin || filtroValorMax
                                ? 'Tente ajustar os filtros ou termos de busca.'
                                : 'Comece cadastrando seu primeiro imóvel.'}
                        </p>
                        {!searchTerm && filtroTipo === 'todos' && filtroCategoria === 'todos' &&
                            filtroConstrutora === 'todos' && filtroEstado === 'todos' &&
                            filtroCidade === 'todos' && !filtroValorMin && !filtroValorMax && (
                                <Button
                                    variant="btn_add"
                                    className="px-8 py-3"
                                >
                                    Criar Primeiro Imóvel
                                </Button>
                            )}
                    </div>
                ) : (
                    <>
                        {/* Visualização de Imóveis */}
                        {viewMode === 'cards' ? (
                            <ImoveisCardView
                                imoveis={filteredImoveis.slice(0, itemsToShow)}
                                onView={handleView}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                formatDate={formatDate}
                            />
                        ) : (
                            <ImoveisListView
                                imoveis={sortedImoveis.slice(0, itemsToShow)}
                                onView={handleView}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                formatDate={formatDate}
                                searchTerm={searchTerm}
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                visibleColumns={visibleColumns}
                                onToggleColumn={handleToggleColumn}
                            />
                        )}

                        {/* Botão Visualizar Mais */}
                        {filteredImoveis.length > itemsToShow && (
                            <div className="mt-6 flex justify-center">
                                <Button
                                    variant="btn_secondary"
                                    onClick={() => setItemsToShow(prev => prev + 12)}
                                    className="px-6 py-3"
                                >
                                    Visualizar Mais ({filteredImoveis.length - itemsToShow} restantes)
                                </Button>
                            </div>
                        )}

                        {/* Informação de itens exibidos */}
                        {filteredImoveis.length > 0 && (
                            <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                                Exibindo {Math.min(itemsToShow, filteredImoveis.length)} de {filteredImoveis.length} imóveis
                            </div>
                        )}
                    </>
                )}

                {/* Toast Container */}
                <ToastContainer toasts={toasts} />
            </div>
        </div>
    )
}

